/**
 * Per-page history manager for content editor.
 * Tracks text edits, style changes, formula edits, and image replacements
 * within a single page's iframe.
 */

import type { MessageBridge } from './message-bridge'
import { log, setInlineStyleImportant } from './utils'

export type ContentOpType =
  | 'text-edit'
  | 'compound'
  | 'style'
  | 'formula'
  | 'replace-image'
  | 'image-compound'

export interface StyleChange {
  property: string,
  oldValue: string,
  newValue: string,
}

export interface ContentHistoryOperation {
  type: ContentOpType,
  elementId: string,
  timestamp: number,
  oldText?: string,
  newText?: string,
  oldHtml?: string,
  newHtml?: string,
  details?: { property: string, value: string },
  oldDetails?: { property: string, value: string },
  oldSrc?: string,
  newSrc?: string,
  styleChanges?: StyleChange[],
}

export class HistoryManager {
  private operations: ContentHistoryOperation[] = []

  private currentIndex = -1

  private maxSize: number

  private bridge: MessageBridge | null

  private initialTextMap = new Map<string, string>()

  constructor(maxSize: number, bridge: MessageBridge | null = null) {
    this.maxSize = maxSize
    this.bridge = bridge
  }

  saveInitialText(elementId: string, text: string): void {
    if (!this.initialTextMap.has(elementId)) {
      this.initialTextMap.set(elementId, text)
    }
  }

  getInitialText(elementId: string): string | undefined {
    return this.initialTextMap.get(elementId)
  }

  addOperation(operation: Omit<ContentHistoryOperation, 'timestamp'>): void {
    if (this.currentIndex < this.operations.length - 1) {
      this.operations = this.operations.slice(0, this.currentIndex + 1)
    }

    this.operations.push({ ...operation, timestamp: Date.now() })

    if (this.operations.length > this.maxSize) {
      this.operations.shift()
    } else {
      this.currentIndex++
    }

    this.notifyChange()
    log('添加操作记录', {
      type: operation.type,
      elementId: operation.elementId,
      currentIndex: this.currentIndex,
    })
  }

  undo(): boolean {
    if (!this.canUndo()) {
      log('无法撤销：已到达历史记录起点或无操作记录')
      return false
    }

    const operation = this.operations[this.currentIndex]
    this.revertOperation(operation)
    this.currentIndex--
    this.notifyChange()
    return true
  }

  redo(): boolean {
    if (!this.canRedo()) {
      log('无法重做：已到达历史记录终点')
      return false
    }

    this.currentIndex++
    const operation = this.operations[this.currentIndex]
    this.applyOperation(operation)
    this.notifyChange()
    return true
  }

  canUndo(): boolean {
    return this.currentIndex >= 0 && this.operations.length > 0
  }

  canRedo(): boolean {
    return this.currentIndex < this.operations.length - 1
  }

  restoreAll(): void {
    log('开始恢复所有元素到初始状态', { totalElements: this.initialTextMap.size })

    for (let i = this.currentIndex; i >= 0; i--) {
      const op = this.operations[i]
      const element = document.querySelector(`[data-musk-element-id="${op.elementId}"]`)
      if (!element) continue

      if (op.type === 'style' && op.oldDetails) {
        setInlineStyleImportant(element as HTMLElement, op.oldDetails.property, op.oldDetails.value)
      } else if (op.type === 'formula') {
        const temp = document.createElement('div')
        temp.innerHTML = op.oldHtml || ''
        const restored = temp.firstChild
        if (restored && element.parentNode) element.parentNode.replaceChild(restored, element)
      } else if (op.type === 'image-compound') {
        if ((element as HTMLImageElement).tagName === 'IMG' && op.oldSrc) {
          (element as HTMLImageElement).src = op.oldSrc
        }
        if (op.styleChanges) {
          for (const sc of op.styleChanges) {
            setInlineStyleImportant(element as HTMLElement, sc.property, sc.oldValue)
          }
        }
      }
    }

    let restoredCount = 0
    this.initialTextMap.forEach((initialText, elementId) => {
      const element = document.querySelector(`[data-musk-element-id="${elementId}"]`)
      if (element) {
        element.textContent = initialText
        restoredCount++
      }
    })

    this.operations = []
    this.currentIndex = -1
    this.notifyChange()

    log('恢复完成', { restoredCount, totalElements: this.initialTextMap.size })
  }

  clear(): void {
    this.operations = []
    this.currentIndex = -1
    this.notifyChange()
  }

  private revertOperation(op: ContentHistoryOperation): void {
    const element = document.querySelector(`[data-musk-element-id="${op.elementId}"]`)
    if (!element) {
      log('元素未找到，无法恢复操作', { elementId: op.elementId })
      return
    }

    switch (op.type) {
      case 'compound':
        element.innerHTML = op.oldText || ''
        if (op.styleChanges) {
          for (const sc of op.styleChanges) {
            setInlineStyleImportant(element as HTMLElement, sc.property, sc.oldValue)
          }
        }
        break
      case 'text-edit':
        element.innerHTML = op.oldText || ''
        break
      case 'style':
        if (op.oldDetails) {
          setInlineStyleImportant(element as HTMLElement, op.oldDetails.property, op.oldDetails.value)
        }
        break
      case 'formula': {
        const temp = document.createElement('div')
        temp.innerHTML = op.oldHtml || ''
        const restored = temp.firstChild
        if (restored && element.parentNode) element.parentNode.replaceChild(restored, element)
        break
      }
      case 'replace-image': {
        const oldSrc = op.oldDetails && (op.oldDetails as Record<string, string>).oldValue
        if (oldSrc && element.tagName === 'IMG') {
          (element as HTMLImageElement).src = oldSrc
        }
        break
      }
      case 'image-compound':
        if (element.tagName === 'IMG' && op.oldSrc) {
          (element as HTMLImageElement).src = op.oldSrc
        }
        if (op.styleChanges) {
          for (const sc of op.styleChanges) {
            setInlineStyleImportant(element as HTMLElement, sc.property, sc.oldValue)
          }
        }
        break
    }
  }

  private applyOperation(op: ContentHistoryOperation): void {
    const element = document.querySelector(`[data-musk-element-id="${op.elementId}"]`)
    if (!element) {
      log('元素未找到，无法应用操作', { elementId: op.elementId })
      return
    }

    switch (op.type) {
      case 'compound':
        element.innerHTML = op.newText || ''
        if (op.styleChanges) {
          for (const sc of op.styleChanges) {
            setInlineStyleImportant(element as HTMLElement, sc.property, sc.newValue)
          }
        }
        break
      case 'text-edit':
        element.innerHTML = op.newText || ''
        break
      case 'style':
        if (op.details) {
          setInlineStyleImportant(element as HTMLElement, op.details.property, op.details.value)
        }
        break
      case 'formula': {
        const temp = document.createElement('div')
        temp.innerHTML = op.newHtml || ''
        const applied = temp.firstChild
        if (applied && element.parentNode) element.parentNode.replaceChild(applied, element)
        break
      }
      case 'replace-image': {
        const newSrc = op.details && (op.details as Record<string, string>).newValue
        if (newSrc && element.tagName === 'IMG') {
          (element as HTMLImageElement).src = newSrc
        }
        break
      }
      case 'image-compound':
        if (element.tagName === 'IMG' && op.newSrc) {
          (element as HTMLImageElement).src = op.newSrc
        }
        if (op.styleChanges) {
          for (const sc of op.styleChanges) {
            setInlineStyleImportant(element as HTMLElement, sc.property, sc.newValue)
          }
        }
        break
    }
  }

  private notifyChange(): void {
    this.bridge?.send('HISTORY_CHANGED', {
      canUndo: this.canUndo(),
      canRedo: this.canRedo(),
      historyLength: this.operations.length,
      currentIndex: this.currentIndex,
    })
  }
}
